'use client'

import { useState } from 'react'

import { ChevronDown } from 'lucide-react'

import { formatDuration, formatPrice } from '@/lib/c2c/format'
import type { EnhancedCarrierData } from '@/lib/c2c/types'
import { sanitizeHtml } from '@/lib/sanitize-html'
import { cn } from '@/lib/utils'
import { Link } from '@/i18n/navigation'

interface CarrierSectionProps {
    carriers: EnhancedCarrierData[]
    mode: string
    title?: string
    description?: string
}

function CarrierAccordion({ carrier }: { carrier: EnhancedCarrierData }) {
    const [open, setOpen] = useState(false)

    if (!carrier.aboutHtml) {
        return null
    }

    return (
        <li className="border border-[#d9dee5] first:rounded-t-[7px] last:rounded-b-[7px] [&:not(:first-child)]:-mt-px">
            <button
                type="button"
                className="flex w-full items-center gap-3 px-4 py-4"
                aria-expanded={open}
                onClick={() => setOpen((o) => !o)}
            >
                {carrier.logoUrl && (
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center">
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img src={carrier.logoUrl} alt="" className="h-10 w-10 object-contain" />
                    </span>
                )}
                <span className="text-base text-[#44474a]">{carrier.name}</span>
                <ChevronDown
                    className={cn(
                        'ml-auto h-6 w-6 shrink-0 text-[#44474a] transition-transform duration-200',
                        open && 'rotate-180',
                    )}
                />
            </button>
            <div
                className={cn(
                    'grid transition-[grid-template-rows] duration-200 ease-in-out',
                    open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
                )}
            >
                <div className="overflow-hidden">
                    <div className="px-4 pb-4">
                        <div
                            className="prose max-w-none text-base leading-6 text-[#44474a] [&_a]:text-[#00bfd5] [&_a:hover]:text-[#008a99]"
                            dangerouslySetInnerHTML={{ __html: sanitizeHtml(carrier.aboutHtml) }}
                        />
                        {carrier.images.length > 0 && (
                            <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
                                {carrier.images.map((img) => (
                                    // eslint-disable-next-line @next/next/no-img-element
                                    <img
                                        key={img.src}
                                        src={img.src}
                                        alt={img.alt}
                                        className="h-auto w-full rounded object-cover"
                                        loading="lazy"
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </li>
    )
}

/**
 * CarrierSection displays carrier stats in a table layout matching production.
 * Shows carrier name (linked), daily trips, avg time, and avg price.
 * Below the table, carrier accordion cards show expanded about info.
 * Returns null when no carriers are available.
 */
export function CarrierSection({ carriers, mode, title, description }: CarrierSectionProps) {
    if (carriers.length === 0) {
        return null
    }

    const modeLabel = mode.charAt(0).toUpperCase() + mode.slice(1)
    const sectionTitle = title ?? `${modeLabel} Companies`
    const carriersWithAbout = carriers.filter((c) => c.aboutHtml)

    return (
        <section id="carriers" data-section="carriers" className="my-[72px] md:my-24">
            <h2 className="mb-6 text-[20px] font-bold text-[#44474a] md:text-2xl">
                {sectionTitle}
            </h2>

            {description && <p className="mb-8 text-base text-[#44474a]">{description}</p>}

            <div className="mb-8 overflow-x-auto">
                <table className="w-full border-collapse text-left">
                    <thead>
                        <tr className="text-sm text-[#707782] uppercase">
                            <th className="pt-4 pb-3 pl-4 font-bold">{modeLabel.toUpperCase()}</th>
                            <th className="pt-4 pb-3 pl-4 font-bold">Daily Trips</th>
                            <th className="pt-4 pb-3 pl-4 font-bold">Avg. Time</th>
                            <th className="pt-4 pb-3 pl-4 font-bold">Avg. Price</th>
                        </tr>
                    </thead>
                    <tbody>
                        {carriers.map((carrier, index) => (
                            <tr key={carrier.code}>
                                <td
                                    className={`min-w-[135px] px-4 py-2 ${index % 2 === 0 ? 'rounded-l-[7px] bg-[#f4f5f7]' : ''}`}
                                >
                                    {carrier.guideUrl ? (
                                        <Link
                                            href={carrier.guideUrl}
                                            className="font-medium text-[#00bfd5] hover:underline"
                                        >
                                            {carrier.name}
                                        </Link>
                                    ) : (
                                        <span className="font-medium">{carrier.name}</span>
                                    )}
                                </td>
                                <td
                                    className={`min-w-[135px] px-4 py-2 ${index % 2 === 0 ? 'bg-[#f4f5f7]' : ''}`}
                                >
                                    {carrier.avgDailyTrips != null
                                        ? Math.round(carrier.avgDailyTrips)
                                        : '—'}
                                </td>
                                <td
                                    className={`min-w-[135px] px-4 py-2 ${index % 2 === 0 ? 'bg-[#f4f5f7]' : ''}`}
                                >
                                    {carrier.avgDurationMinutes != null
                                        ? formatDuration(carrier.avgDurationMinutes)
                                        : '—'}
                                </td>
                                <td
                                    className={`min-w-[135px] px-4 py-2 ${index % 2 === 0 ? 'rounded-r-[7px] bg-[#f4f5f7]' : ''}`}
                                >
                                    {carrier.avgPrice != null
                                        ? formatPrice(carrier.avgPrice)
                                        : carrier.minPrice != null
                                          ? formatPrice(carrier.minPrice)
                                          : '—'}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {carriersWithAbout.length > 0 && (
                <ul className="list-none p-0">
                    {carriersWithAbout.map((carrier) => (
                        <CarrierAccordion key={carrier.code} carrier={carrier} />
                    ))}
                </ul>
            )}
        </section>
    )
}
